import { useEffect, useState } from "react";
import { fetchMemoryGraph } from "@/api/observability";
import CapabilityGate from "@/components/CapabilityGate";
import LockedFeatureCard from "@/components/LockedFeatureCard";
import { Button } from "@/components/ui/button";

export default function MemoryGraph() {
  const [graph, setGraph] = useState<{ episodes: any[]; clusters: any[]; edges: any[] }>({ episodes: [], clusters: [], edges: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = async () => {
    setLoading(true);
    setError(null);
    try {
      const payload = await fetchMemoryGraph();
      setGraph(payload);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="aa-page-title text-3xl font-semibold">
            <span className="aa-gradient-text">Memory Graph</span>
          </h1>
          <p className="text-sm text-muted-foreground">Episodic memory clusters and links.</p>
        </div>
        <Button type="button" variant="ghost" size="sm" className="text-xs" onClick={refresh}>
          Refresh
        </Button>
      </div>
      {error && <p className="text-sm text-rose-600 dark:text-rose-300">{error}</p>}
      <section className="aa-glass-card rounded-2xl p-4 space-y-4">
        {loading ? (
          <p className="text-sm text-muted-foreground">Loading memory graph…</p>
        ) : (
          <>
            <div className="grid gap-4 md:grid-cols-2">
              <article className="aa-glass-card rounded-xl p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Episodes indexed</p>
                <p className="mt-1 text-3xl font-semibold text-foreground">{graph.episodes.length}</p>
              </article>
              <article className="aa-glass-card rounded-xl p-4">
                <p className="text-xs uppercase tracking-wide text-muted-foreground">Clusters detected</p>
                <p className="mt-1 text-3xl font-semibold text-foreground">{graph.clusters.length}</p>
              </article>
            </div>
            <CapabilityGate
              flag="canUseMemoryGraph"
              fallback={<LockedFeatureCard flag="canUseMemoryGraph" />}
            >
              <div className="grid gap-4 md:grid-cols-2">
                <div>
                  <h2 className="aa-page-title mb-2 text-sm font-semibold">
                    Episodes ({graph.episodes.length})
                  </h2>
                  <div className="max-h-72 overflow-y-auto space-y-2">
                    {graph.episodes.map((episode, idx) => (
                      <article
                        key={episode.id ?? idx}
                        className="aa-glass-card rounded-xl p-3"
                      >
                        <div className="text-xs text-muted-foreground">{episode.agent}</div>
                        <div className="text-sm font-semibold text-foreground">
                          {episode.summary ?? "(no summary)"}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
                <div>
                  <h2 className="aa-page-title mb-2 text-sm font-semibold">
                    Clusters ({graph.clusters.length})
                  </h2>
                  <div className="max-h-72 overflow-y-auto space-y-2">
                    {graph.clusters.map((cluster, idx) => (
                      <article
                        key={cluster.name ?? idx}
                        className="aa-glass-card rounded-xl p-3"
                      >
                        <div className="text-xs text-muted-foreground">{cluster.agent}</div>
                        <div className="text-sm font-semibold text-foreground">{cluster.name}</div>
                        <div className="text-xs text-muted-foreground">
                          Episodes: {cluster.episode_ids?.length ?? 0}
                        </div>
                      </article>
                    ))}
                  </div>
                </div>
              </div>
            </CapabilityGate>
          </>
        )}
      </section>
    </div>
  );
}
